import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createTRPCClient, TRPCClientError } from "@trpc/client";
import { httpBatchLink } from "@trpc/client";
import type { FC, PropsWithChildren } from "react";
import { useCallback, useMemo } from "react";
import { TRPCProvider } from "../trpc";
import type { RootRouter } from "@project/api";
import { router } from "../App";
import { clearAccountNameFromSession } from "@project/common";

const MAX_RETRIES = 3;

const retry = (failureCount: number, error: Error | TRPCClientError<RootRouter>) => {
  if (failureCount > MAX_RETRIES) {
    return false;
  }
  if (error instanceof TRPCClientError) {
    const {
      data,
    } = error as TRPCClientError<RootRouter>;
    if (
      data?.httpStatus === 401
      || data?.httpStatus === 400
      || data?.httpStatus === 404
    ) {
      return false;
    }
  }
  return true;
};

export const QueryProvider: FC<PropsWithChildren> = ({ children }) => {
  const onError = useCallback((error: Error | TRPCClientError<RootRouter>) => {
    if (error instanceof TRPCClientError) {
      const {
        data,
      } = error as TRPCClientError<RootRouter>;

      if (data?.httpStatus === 401) {
        void router.navigate({ to: "/sign-in" }).then(() => {
          clearAccountNameFromSession();
        });
      }
      else {
        logger.error({
          error,
        }, "TRPC client error");
      }
    }
  }, []);

  const queryClient = useMemo(() => {
    return new QueryClient({
      defaultOptions: {
        queries: {
          retry,
        },
      },
      queryCache: new QueryCache({
        onError,
      }),
      mutationCache: new MutationCache({
        onError,
      }),
    });
  }, [onError]);

  const trpcClient = useMemo(() =>
    createTRPCClient<RootRouter>({
      links: [
        httpBatchLink({
          maxURLLength: 1024,
          url: "/api/trpc",
        }),
      ],
    }),
  []);

  return (
    <QueryClientProvider client={queryClient}>
      <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
        {children}
      </TRPCProvider>
    </QueryClientProvider>
  );
};
